import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { companyTypes } from "@/data/company";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { ComparisonOperator, comparisonOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { match, P } from "ts-pattern";
import { z } from "zod";

const schema = z.object({
  operator: comparisonOperators.enum,
  values: z.array(z.number()).min(1, {
    message: "Du skal vælge mindst en virksomhedsform",
  }),
});

export function CompanyTypeSegment({
  queryFilters,
  update,
  formId,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const companyTypeFilter = queryFilters.value;

  const { control, handleSubmit, watch, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      operator: (companyTypeFilter?.operator as ComparisonOperator) || "Equal",
      values: companyTypeFilter?.values?.map((t) => Number(t)) || [],
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const { values, operator } = watch();

  const submit = handleSubmit((data) => {
    const formattedFilter = match(data)
      .with(
        { operator: P.union("Equal", "NotEqual"), values: P.array(P.number) },
        (filter) => ({
          operator: filter.operator,
          values: filter.values.map(String),
        }),
      )
      .otherwise(() => undefined);

    update({
      value: formattedFilter,
    });
  });

  return (
    <>
      <SegmentHeader
        title="Virksomhedsform"
        description="Vælg de virksomhedsformer du ønsker skal indgå i din målgruppe, såsom ApS og A/S."
        className="mb-9"
      />
      <Form
        className="flex h-full flex-col items-center justify-between gap-9"
        onSubmit={submit}
        id={formId}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col">
            <div className="flex w-full justify-center gap-4">
              <Controller
                control={control}
                name="operator"
                render={({ field, fieldState }) => (
                  <Select
                    aria-label="Sammenligningsoperator"
                    selectedKey={field.value}
                    onSelectionChange={field.onChange}
                    items={Object.entries(comparisonOperators.values).map(
                      ([key, value]) => ({
                        id: key,
                        label: value,
                      }),
                    )}
                    errorMessage={fieldState.error?.message}
                  >
                    {(item) => (
                      <SelectItem id={item.id}>{item.label}</SelectItem>
                    )}
                  </Select>
                )}
              />
              <div>
                <Controller
                  control={control}
                  name="values"
                  render={({ field, fieldState }) => (
                    <TagField
                      placeholder="Virksomhedsform"
                      selectedOptions={
                        field.value.map((item) => ({
                          label:
                            companyTypes.find((type) => type.value === item)
                              ?.label || "",
                          value: item,
                        })) || []
                      }
                      setSelectedOptions={(value) => {
                        if (Array.isArray(value))
                          return field.onChange(
                            value.map((item) => item.value),
                          );
                      }}
                      options={companyTypes}
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <ul className="flex w-full flex-col gap-y-2">
            {values.map((value, index) => (
              <ListItem
                key={value}
                label={`${operator === "Equal" ? "Lig med" : "Ikke lig med"} ${
                  companyTypes.find((type) => type.value === Number(value))
                    ?.label
                }`}
                onRemove={() => {
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  setValue("values", newValues);
                }}
              />
            ))}
          </ul>
        </div>
      </Form>
    </>
  );
}
